'use client';

import { useParams } from 'next/navigation';

import { DEFAULT_LOCALE } from '@/constants/locales';
import { useLocalizedRoutes } from '@/context/LocalizedRoutesContext';
import { localizeHref } from '@/utils/localize-href';

// Describes where a raw href came from so we can localize it correctly.
export interface LocalizedHrefSource {
  // True when the href comes from a CMS page reference (carries its own locale).
  fromPageReference?: boolean;
  // The referenced page's own locale — authoritative for page-reference links.
  referenceLocale?: string;
}

// Resolves an internal CMS href to the correct locale-prefixed URL for the active locale.
// Page references use their own locale (an English-only reference stays unprefixed);
// manually-authored URLs are prefixed only when the path is an actually-localized page
// in the active locale, so English-only links typed on a Spanish page keep their
// unprefixed default-locale URL instead of a broken /es prefix.
export function useLocalizedHref(
  rawHref: string | undefined,
  source: LocalizedHrefSource = {}
): string | undefined {
  const params = useParams();
  const currentLocale = (params?.locale as string | undefined) ?? DEFAULT_LOCALE;
  const { localizedUrls } = useLocalizedRoutes();

  if (!rawHref) return rawHref;

  // Page reference: prefix only when the referenced page is localized in the active
  // locale. A differing locale means it only exists there (e.g. English-only) — leave raw.
  if (source.fromPageReference) {
    return source.referenceLocale && source.referenceLocale !== currentLocale
      ? rawHref
      : localizeHref(rawHref, currentLocale);
  }

  // Manual URL with no locale context. When the routes set is unavailable (index not
  // built), fall back to prefixing so existing localized links don't regress.
  if (localizedUrls.size === 0) return localizeHref(rawHref, currentLocale);

  // External/hash/mailto and already-prefixed paths are handled by localizeHref as no-ops;
  // for internal relative paths, prefix only when the path exists in the active locale.
  if (!rawHref.startsWith('/')) return localizeHref(rawHref, currentLocale);
  return localizedUrls.has(rawHref.toLowerCase())
    ? localizeHref(rawHref, currentLocale)
    : rawHref;
}
